/**
 * チーム週次MTG の API クライアント。
 *
 * 画面も MCP（`mcp-org-issues` の `team_mtg_*`）も同じ口（product-api の `/team-meetings`）を叩く。
 * **判定は API 側の 1 本**にあるので、ここには上限・必須条件・権限・開催回の日付の規則を写さない。
 * ボタンの出し分けは API が返す `permissions` を見て、弾かれたら `ApiError.message` をそのまま出す。
 *
 * 形は `@cortex/domain-team-meeting` の型をそのまま使う（`import type` なので Firestore はバンドルに
 * 入らない）。自動ブロックは product-api が組み立てる形なので、画面が読む項目だけをここに書く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チーム週次MTG のシリーズ・開催回・自動ブロック・投稿・いいね・棚卸しを扱う API クライアント
 * @graph-connects /team-meetings [calls] 週次MTG API
 */

import type { Action } from '@cortex/domain-org-issues';
import type { OrgRef, Visibility } from '@cortex/domain-org-scope';
import type {
  AutoBlockKey,
  Meeting,
  NextAction,
  OwnerCandidates,
  Post,
  PostPatch,
  PostPermissions,
  Series,
  SeriesPermissions,
} from '@cortex/domain-team-meeting';
import type { OrgIssuePeople } from './org-issues.js';
import { request } from './_request.js';

export type { OwnerCandidates };

/** シリーズと、それに対してできること。 @graph-connects none */
export interface SeriesView {
  series: Series;
  permissions: SeriesPermissions;
}

/** シリーズの詳細。 @graph-connects none */
export interface SeriesDetail extends SeriesView {
  /** 開催回（新しい順） */
  meetings: Meeting[];
  /** 次に開く開催回の日付（まだ 1 度も棚卸ししていなければ今日） */
  currentMeetingOn: string;
  people: OrgIssuePeople;
}

/** 投稿と、それに対してできること。 @graph-connects none */
export interface PostView {
  post: Post;
  permissions: PostPermissions;
}

/** 持ち越しの Next 1 件。 @graph-connects none */
export interface CarriedAction {
  issue: { id: string; title: string; columnId: string };
  action: Action;
}

/** 開催回 1 つ分。 @graph-connects none */
export interface MeetingDetail extends SeriesView {
  meetingId: string;
  /** まだ棚卸ししていない今の開催回なら `null` */
  meeting: Meeting | null;
  /** まだ棚卸ししていない投稿（全部・古い順） */
  openPosts: PostView[];
  /** この開催回で棚卸しした投稿 */
  triagedPosts: PostView[];
  carriedActions: CarriedAction[];
  people: OrgIssuePeople;
}

/** KPI の日次の点（画面が読む項目だけ）。 @graph-connects none */
export interface KpiTrendPoint {
  date: string;
  bugRate: number | null;
  developPointMtd: number;
  developPointMtdTarget?: number;
  releaseCountMtd: number;
  releaseCountMtdTarget?: number;
}

/** ブロックに出す案件。 @graph-connects none */
export interface AutoBlockProject {
  issueKey: string;
  summary: string;
  status: string;
  dueDate: string | null;
  assignee: string | null;
  devLeader: string | null;
}

/** 自動ブロック 1 つ。出せない・失敗したものは理由つき。 @graph-connects none */
export type AutoBlock =
  | { key: 'kpi'; status: 'ok'; team: string; trend: { data: KpiTrendPoint[] } }
  | {
      key: 'holiday_ops';
      status: 'ok';
      covers: {
        date: string;
        weekday: string;
        service: string;
        serviceLabel: string;
        who: string;
        userId: string | null;
      }[];
    }
  | { key: 'active_projects' | 'upcoming_releases'; status: 'ok'; projects: AutoBlockProject[] }
  | {
      key: 'released_projects';
      status: 'ok';
      releases: {
        releasedDate: string;
        issueKey: string;
        summary: string;
        assignee: string | null;
      }[];
    }
  | { key: AutoBlockKey; status: 'unavailable' | 'error'; reason: string };

/** シリーズ作成の入力（画面は組織単位の所有・公開範囲だけを出す）。 @graph-connects none */
export interface SeriesCreateRequest {
  name: string;
  owner: Exclude<OrgRef, { kind: 'users' }>;
  visibility: Exclude<Visibility, { kind: 'users' }>;
  defaultBoardId: string | null;
}

/** 投稿の指定（投稿は開催回に属さない）。 @graph-connects none */
export interface PostRef {
  seriesId: string;
  postId: string;
}

/** シリーズの path。 @graph-connects none */
function seriesPath(seriesId: string): string {
  return `/team-meetings/series/${encodeURIComponent(seriesId)}`;
}

/** 開催回の path。 @graph-connects none */
function meetingPath(seriesId: string, meetingId: string): string {
  return `${seriesPath(seriesId)}/meetings/${encodeURIComponent(meetingId)}`;
}

/** 投稿の path。 @graph-connects none */
function postPath(ref: PostRef): string {
  return `${seriesPath(ref.seriesId)}/posts/${encodeURIComponent(ref.postId)}`;
}

/** JSON を載せた書き込み。 @graph-connects none */
function send(method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): RequestInit {
  return body === undefined ? { method } : { method, body: JSON.stringify(body) };
}

/** 投稿 1 件の返り。 @graph-connects none */
type PostResponse = PostView & { people: OrgIssuePeople };

/** 週次MTG API。 @graph-connects none */
export const teamMeetingsApi = {
  /** @graph-connects /team-meetings/series [calls] 見られるシリーズの一覧 */
  listSeries: (): Promise<{
    series: SeriesView[];
    /** 自分がシリーズを作れる所有組織のコード */
    ownerCandidates: OwnerCandidates;
    people: OrgIssuePeople;
  }> => request('/team-meetings/series'),

  /** @graph-connects /team-meetings/series [calls] シリーズの作成 */
  createSeries: (input: SeriesCreateRequest): Promise<SeriesView & { people: OrgIssuePeople }> =>
    request('/team-meetings/series', send('POST', input)),

  /** @graph-connects /team-meetings/series/{seriesId} [calls] シリーズの詳細 */
  getSeries: (seriesId: string): Promise<SeriesDetail> => request(seriesPath(seriesId)),

  /** @graph-connects /team-meetings/series/{seriesId}/meetings/{meetingId} [calls] 開催回 */
  getMeeting: (seriesId: string, meetingId: string): Promise<MeetingDetail> =>
    request(meetingPath(seriesId, meetingId)),

  /** @graph-connects /team-meetings/series/{seriesId}/meetings/{meetingId}/auto-blocks [calls] 自動ブロック */
  getAutoBlocks: (
    seriesId: string,
    meetingId: string,
  ): Promise<{ meetingId: string; blocks: AutoBlock[]; people: OrgIssuePeople }> =>
    request(`${meetingPath(seriesId, meetingId)}/auto-blocks`),

  /** @graph-connects /team-meetings/series/{seriesId}/posts [calls] 投稿 */
  createPost: (
    seriesId: string,
    input: { category: string; body: string },
  ): Promise<PostResponse> => request(`${seriesPath(seriesId)}/posts`, send('POST', input)),

  /** @graph-connects /team-meetings/series/{seriesId}/posts/{postId} [calls] 投稿の書き換え */
  updatePost: (ref: PostRef, patch: PostPatch): Promise<PostResponse> =>
    request(postPath(ref), send('PATCH', patch)),

  /** @graph-connects /team-meetings/series/{seriesId}/posts/{postId} [calls] 投稿の削除 */
  deletePost: (ref: PostRef): Promise<{ deleted: true }> =>
    request(postPath(ref), { method: 'DELETE' }),

  /** @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/like [calls] いいね */
  toggleLike: (ref: PostRef): Promise<PostResponse> =>
    request(`${postPath(ref)}/like`, send('POST')),

  /** @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/triage [calls] 棚卸し */
  triage: (ref: PostRef, nextAction: NextAction): Promise<PostResponse> =>
    request(`${postPath(ref)}/triage`, send('POST', nextAction)),

  /** @graph-connects /team-meetings/series/{seriesId}/posts/{postId}/triage [calls] 棚卸しを戻す */
  reopen: (ref: PostRef): Promise<PostResponse> =>
    request(`${postPath(ref)}/triage`, send('DELETE')),
};
